import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Skeleton, Typography, Box } from '@mui/material';


interface GDataTableProps {
    data?: any[];
    loading?: boolean;
    error?: string | null;
}


/**
 * GDataTable - Generic table component for displaying table data
 * Dynamically renders columns based on data structure
 */
const GDataTable: React.FC<GDataTableProps> = ({ data = [], loading = false, error = null }) => {
    const themedContainerSx = {
        backgroundColor: 'var(--background-color)',
        color: 'var(--text-color)',
        border: '1px solid var(--border-color, #e2e8f0)',
    };

    if (error) {
        return (
            <Box sx={{ padding: 2, color: 'var(--text-color)' }}>
                <Typography color="error">Error loading data: {error}</Typography>
            </Box>
        );
    }

    if (loading) {
        return (
            <TableContainer component={Paper} sx={themedContainerSx}>
                <Table>
                    <TableBody>
                        {[...Array(5)].map((_, i) => (
                            <TableRow key={i}>
                                <TableCell sx={{ borderColor: 'var(--border-color, #e2e8f0)' }}>
                                    <Skeleton sx={{ bgcolor: 'var(--hover-bg, #f3f4f6)' }} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Box sx={{ padding: 2, color: 'var(--text-color)' }}>
                <Typography>No data available</Typography>
            </Box>
        );
    }

    // Extract column names from first data row
    const columns = Object.keys(data[0]);

    return (
        <TableContainer component={Paper} sx={themedContainerSx}>
            <Table>
                <TableHead>
                    <TableRow sx={{ backgroundColor: 'var(--table-header-bg, #1976d2)' }}>
                        {columns.map((column) => (
                            <TableCell
                                key={column}
                                sx={{
                                    fontWeight: 600,
                                    color: 'var(--table-header-text, #fff)',
                                    backgroundColor: 'var(--table-header-bg, #1976d2)',
                                    borderColor: 'var(--border-color, #e2e8f0)',
                                    textTransform: 'uppercase',
                                    fontSize: 12,
                                    letterSpacing: '0.03em',
                                }}
                            >
                                {column}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.map((row, rowIndex) => (
                        <TableRow
                            key={rowIndex}
                            hover
                            sx={{
                                '&:hover': {
                                    backgroundColor: 'var(--hover-bg, #f3f4f6)',
                                },
                            }}
                        >
                            {columns.map((column) => (
                                <TableCell
                                    key={`${rowIndex}-${column}`}
                                    sx={{
                                        color: 'var(--text-color)',
                                        backgroundColor: 'var(--background-color)',
                                        borderColor: 'var(--border-color, #e2e8f0)',
                                    }}
                                >
                                    {String(row[column] ?? '').substring(0, 100)}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default GDataTable;
